import { X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { enterTransition, exitTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";

type DialogContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialogContext() {
  const context = useContext(DialogContext);
  if (!context) throw new Error("Dialog parts must be used inside Dialog.");
  return context;
}

export function Dialog({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const isOpen = open ?? uncontrolled;
  const setOpen = (next: boolean) => {
    if (open === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };

  return (
    <DialogContext.Provider value={{ open: isOpen, setOpen }}>
      <DialogPrimitive.Root open={isOpen} onOpenChange={setOpen}>
        {children}
      </DialogPrimitive.Root>
    </DialogContext.Provider>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

type FocusAndDismiss = Pick<
  DialogPrimitive.DialogContentProps,
  "onOpenAutoFocus" | "onCloseAutoFocus" | "onEscapeKeyDown" | "onInteractOutside"
>;

export function DialogContent({
  title,
  description,
  children,
  className,
  hideClose = false,
  ...focusAndDismiss
}: {
  title: string;
  description?: string;
  children?: ReactNode;
  className?: string;
  /** For a dialog whose own buttons cancel it. */
  hideClose?: boolean;
} & FocusAndDismiss) {
  const { open, setOpen } = useDialogContext();
  const reduce = useReducedMotion();
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);

  useEffect(() => {
    if (open) return;
    // Reduced motion: no exit to watch, so it leaves at once.
    const timeout = window.setTimeout(() => setMounted(false), reduce ? 0 : 220);
    return () => window.clearTimeout(timeout);
  }, [open, reduce]);

  if (!mounted) return null;

  const hidden = reduce
    ? { opacity: 0, x: "-50%", y: "-50%", scale: 1 }
    : { opacity: 0, x: "-50%", y: "-50%", scale: 0.98 };
  const shown = { opacity: 1, x: "-50%", y: "-50%", scale: 1 };

  return (
    <DialogPrimitive.Portal forceMount>
      <DialogPrimitive.Overlay asChild forceMount>
        <motion.div
          className="fixed inset-0 z-50 bg-[oklch(0%_0_0_/_0.42)]"
          style={{ pointerEvents: open ? "auto" : "none" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: open ? 1 : 0 }}
          transition={open ? enterTransition(reduce) : exitTransition(reduce)}
        />
      </DialogPrimitive.Overlay>
      <DialogPrimitive.Content asChild forceMount {...focusAndDismiss}>
        <motion.div
          role="dialog"
          aria-modal="true"
          // Closing, it is on its way out: nothing in it can be reached.
          aria-hidden={!open || undefined}
          inert={!open}
          data-slot="dialog"
          className={cn(
            "fixed top-1/2 left-1/2 z-50 w-[min(28rem,calc(100vw-2rem))] rounded-surface border border-line-subtle bg-surface-raised p-6 shadow-floating outline-none",
            className,
          )}
          style={{ pointerEvents: open ? "auto" : "none" }}
          initial={hidden}
          animate={open ? shown : hidden}
          transition={open ? enterTransition(reduce) : exitTransition(reduce)}
        >
          <div className="flex items-start justify-between gap-4">
            <DialogPrimitive.Title className="font-display text-xl tracking-[-0.03em] text-foreground">
              {title}
            </DialogPrimitive.Title>
            {hideClose ? null : (
              <DialogPrimitive.Close
                className="grid size-11 place-items-center rounded-pill text-secondary can-hover:hover:bg-surface-muted"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <X className="size-4" strokeWidth={1.75} />
              </DialogPrimitive.Close>
            )}
          </div>
          {description ? (
            <DialogPrimitive.Description className="mt-2 text-sm text-secondary">
              {description}
            </DialogPrimitive.Description>
          ) : (
            <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
          )}
          <div className="mt-5">{children}</div>
        </motion.div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
